import { TooltipDirective } from '../../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../../componentes/botones';
import { DtCardComponent } from '../../../../../../componentes/detalle/detalle.components';
import { nivelPerfil } from '../../../../../../auxiliares/iconos/iconos';
import { DatePipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription, from, map, switchMap } from 'rxjs';
import {
  EstadoDownlinkJob,
  IComando,
  IDownlinkJob,
  IEstadoComando,
  IFilter,
  IIntentoDownlink,
  IQueryParam,
  OrigenDownlinkJob,
} from 'modelos/src';
import { ComandosService } from '../../../../../../auxiliares/servicios/http/comandos.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { ILuminariaExtendida } from '../../../luminarias.types';

// Un ítem de la cronología: un downlink job MANUAL (acción de usuario) +
// su comando del dispositivo (join por idComando). El job cuenta la historia
// del TRANSPORTE (cola/BullMQ); el comando la del DISPOSITIVO (recibido/ejecutado).
// A diferencia de la cronología de perfil, acá NO hay ramas SET→AutoGET: cada
// acción manual (encender/apagar/modo/reset…) es un downlink suelto. Se listan
// como línea de tiempo descendente, con el NIVEL desde el que se originó.
interface ItemComando {
  job: IDownlinkJob;
  comando?: IComando;
  accion: string; // "Encender al 75%", "Apagar", "Cambiar modo"…
  esOnOff: boolean; // encender/apagar (resaltado)
}

// Glosario de estados (panel de ayuda desplegable).
interface ItemGlosario {
  estado: string;
  clase: string;
  desc: string;
}

@Component({
  selector: 'app-cronologia-comandos',
  standalone: true,
  imports: [
    TooltipDirective,
    DatePipe,
    MatIconModule,
    BOTONES,
    DtCardComponent,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './cronologia-comandos.component.html',
  styleUrls: ['./cronologia-comandos.component.scss'],
})
export class CronologiaComandosComponent implements OnChanges, OnDestroy {
  private lista2 = inject(Lista2Service);
  private comandosService = inject(ComandosService);

  private readonly INTERVALO_SEG = 15;
  private sub?: Subscription;

  @Input() luminaria: ILuminariaExtendida | null = null;

  cargando = signal(false);
  items = signal<ItemComando[]>([]);
  mostrarAyuda = signal(false);

  // Paginación incremental (la línea de tiempo puede ser larga).
  private readonly PAGINA = 12;
  visibles = signal(this.PAGINA);
  // Ítems con el detalle de reintentos abierto (por job._id).
  intentosAbiertos = signal<Set<string>>(new Set());

  // ─── glosario ───────────────────────────────────────────────

  readonly estadosCola: ItemGlosario[] = [
    {
      estado: 'En fila',
      clase: 'e-pendiente',
      desc: 'Creado, esperando turno en la fila.',
    },
    {
      estado: 'Programado',
      clase: 'e-pendiente',
      desc: 'En fila con delay (reprogramado por el rate limiter).',
    },
    {
      estado: 'Enviando',
      clase: 'e-pendiente',
      desc: 'El worker tomó el job y lo está despachando.',
    },
    {
      estado: 'Enviado',
      clase: 'e-ok',
      desc: 'Despachado a Chirpstack/NS correctamente.',
    },
    {
      estado: 'Confirmado',
      clase: 'e-ok',
      desc: 'Asociado a un comando con estado terminal favorable.',
    },
    {
      estado: 'Descartado',
      clase: 'e-cancelado',
      desc: 'Dispositivo offline o NS sin sesión al momento de enviar.',
    },
    {
      estado: 'Cancelado',
      clase: 'e-cancelado',
      desc: 'Reemplazado por un comando más nuevo con la misma clave.',
    },
    {
      estado: 'Fallido',
      clase: 'e-fallo',
      desc: 'Error técnico tras agotar los reintentos de transporte.',
    },
  ];

  readonly estadosDispositivo: ItemGlosario[] = [
    {
      estado: 'En fila',
      clase: 'e-pendiente',
      desc: 'En la fila del NS, todavía no enviado al dispositivo.',
    },
    {
      estado: 'Enviado',
      clase: 'e-pendiente',
      desc: 'Despachado hacia el dispositivo (estado inicial).',
    },
    {
      estado: 'Recibido',
      clase: 'e-ok',
      desc: 'El dispositivo confirmó la recepción (ACK).',
    },
    {
      estado: 'Ejecutado',
      clase: 'e-ok',
      desc: 'El dispositivo aplicó el comando.',
    },
    {
      estado: 'Ack false',
      clase: 'e-pendiente',
      desc: 'Llegó ACK con acknowledged false (pudo aplicarse igual).',
    },
    {
      estado: 'No Ejecutado',
      clase: 'e-fallo',
      desc: 'Pasó demasiado tiempo y el dispositivo no confirmó / no aplicó.',
    },
    {
      estado: 'Descartado',
      clase: 'e-cancelado',
      desc: 'Descartado por el NS (offline / expiró la ventana).',
    },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['luminaria']) this.cargar();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private get idDispositivo(): string | undefined {
    return this.luminaria?.dispositivo?._id;
  }

  // Polling en vivo (vía Lista2Service). Trae los downlinks MANUALES y, encadenado,
  // sus comandos del dispositivo.
  private cargar(): void {
    this.sub?.unsubscribe();
    const idDispositivo = this.idDispositivo;
    if (!idDispositivo) {
      this.items.set([]);
      return;
    }
    this.cargando.set(true);
    this.visibles.set(this.PAGINA);

    // Solo acciones de usuario (encender/apagar/modo/reset/…). Excluye los
    // downlinks de Reconciliación/Consulta/AutoGet (esos viven en la cronología de perfil).
    const filter = {
      idDispositivoLorawan: idDispositivo,
      origen: 'Manual',
    } as unknown as IFilter<IDownlinkJob>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      limit: 100,
      includeChildren: true,
    };

    this.sub = this.lista2
      .downlinkJobs(query, this.INTERVALO_SEG)
      .pipe(
        switchMap((res) => {
          const jobs = res?.datos ?? [];
          return from(this.cargarComandos(jobs)).pipe(
            map((comandos) => ({ jobs, comandos })),
          );
        }),
      )
      .subscribe({
        next: ({ jobs, comandos }) => {
          this.items.set(this.armar(jobs, comandos));
          this.cargando.set(false);
        },
        error: (e) => {
          console.error('Error cargando cronología de comandos:', e);
          this.items.set([]);
          this.cargando.set(false);
        },
      });
  }

  // Join con los comandos del dispositivo (batch por idComando de cada job + intento).
  private async cargarComandos(
    jobs: IDownlinkJob[],
  ): Promise<Map<string, IComando>> {
    const ids = new Set<string>();
    for (const j of jobs) {
      if (j.idComando) ids.add(j.idComando);
      for (const it of j.intentosLog ?? []) {
        if (it.idComando) ids.add(it.idComando);
      }
    }
    if (!ids.size) return new Map();
    const lista = Array.from(ids);
    const filter = { _id: { $in: lista } } as unknown as IFilter<IComando>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      limit: lista.length,
      includeChildren: true,
    };
    const res = await this.comandosService.getFiltered(query);
    const mapa = new Map<string, IComando>();
    for (const c of res?.datos ?? []) {
      if (c._id) mapa.set(c._id, c);
    }
    return mapa;
  }

  private armar(
    jobs: IDownlinkJob[],
    comandos: Map<string, IComando>,
  ): ItemComando[] {
    return jobs
      .map((job) => {
        const comando = job.idComando ? comandos.get(job.idComando) : undefined;
        return {
          job,
          comando,
          accion: this.accionLabel(job),
          esOnOff: this.esOnOff(job.nombre),
        } as ItemComando;
      })
      .sort((a, b) =>
        (b.job.fechaCreacion ?? '').localeCompare(a.job.fechaCreacion ?? ''),
      );
  }

  private esOnOff(nombre?: string): boolean {
    return nombre === 'Encender' || nombre === 'Apagar';
  }

  // Etiqueta legible de la acción. Toma la descripción del job/comando si la
  // hay (ej. "Encender al 75%"); si no, cae al nombre.
  private accionLabel(job: IDownlinkJob): string {
    if (job.descripcion) return job.descripcion;
    return job.nombre ?? 'Comando';
  }

  // ─── estado COLA (downlink job) ──────────────────────────────

  esExito(estado: EstadoDownlinkJob): boolean {
    return estado === 'Confirmado' || estado === 'Enviado';
  }
  esFallo(estado: EstadoDownlinkJob): boolean {
    return estado === 'Fallido' || estado === 'Descartado';
  }
  // En luminarias se habla de "fila", nunca de "cola" (el valor del backend
  // sigue siendo 'En cola').
  estadoLabel(estado?: EstadoDownlinkJob): string {
    return estado === 'En cola' ? 'En fila' : (estado ?? '');
  }

  estadoClase(estado: EstadoDownlinkJob): string {
    if (this.esExito(estado)) return 'e-ok';
    if (this.esFallo(estado)) return 'e-fallo';
    if (estado === 'Cancelado') return 'e-cancelado';
    return 'e-pendiente';
  }
  estadoIcono(estado: EstadoDownlinkJob): string {
    switch (estado) {
      case 'Confirmado':
        return 'check_circle';
      case 'Enviado':
        return 'send';
      case 'Fallido':
        return 'error';
      case 'Descartado':
        return 'block';
      case 'Cancelado':
        return 'cancel';
      case 'Enviando':
        return 'sync';
      case 'Programado':
        return 'event';
      default:
        return 'pending';
    }
  }

  // ─── estado DISPOSITIVO (comando) ────────────────────────────

  comandoClase(estado?: IEstadoComando): string {
    if (!estado) return 'e-na';
    if (estado === 'Ejecutado' || estado === 'Recibido') return 'e-ok';
    if (estado === 'No Recibido') return 'e-pendiente';
    if (estado === 'No Ejecutado' || estado === 'Descartado') return 'e-fallo';
    return 'e-pendiente';
  }
  comandoLabel(estado?: IEstadoComando): string {
    if (estado === 'No Recibido') return 'Ack false';
    if (estado === 'En Cola') return 'En fila';
    return estado ?? 'Sin información';
  }
  comandoIcono(estado?: IEstadoComando): string {
    switch (estado) {
      case 'Ejecutado':
        return 'task_alt';
      case 'Recibido':
        return 'check_circle';
      case 'Enviado':
        return 'send';
      case 'En Cola':
        return 'pending';
      case 'No Recibido':
      case 'No Ejecutado':
        return 'cancel';
      case 'Descartado':
        return 'block';
      default:
        return 'question_mark';
    }
  }

  // Ícono de la acción (para el nodo de la línea de tiempo).
  accionIcono(nombre?: string): string {
    switch (nombre) {
      case 'Encender':
        return 'lightbulb';
      case 'Apagar':
        return 'light_off';
      case 'Reset':
      case 'Reset Energía':
        return 'restart_alt';
      case 'Pedir GPS':
        return 'my_location';
      default:
        return 'send';
    }
  }

  // ─── etiquetas de procedencia ────────────────────────────────

  origenLabel(origen?: OrigenDownlinkJob): string {
    return origen === 'Manual' ? 'Manual' : (origen ?? '—');
  }

  nivelLabel(nivel?: string): string {
    switch (nivel) {
      case 'luminaria':
        return 'Luminaria';
      case 'grupo':
        return 'Grupo';
      case 'puesta':
        return 'Puesta';
      case 'grupoPuesta':
        return 'Grupo de puestas';
      default:
        return nivel ?? 'Individual';
    }
  }

  // Sin nivel, el comando salió de la propia luminaria.
  nivelIcono(nivel?: string): string {
    return nivelPerfil(nivel ?? 'luminaria').icon;
  }

  // ─── reintentos de transporte ────────────────────────────────

  intentoClase(resultado: string): string {
    if (resultado === 'Enviado') return 'e-ok';
    if (resultado === 'Error' || resultado === 'Descartado') return 'e-fallo';
    return 'e-pendiente';
  }
  intentoIcono(resultado: string): string {
    switch (resultado) {
      case 'Enviado':
        return 'check_circle';
      case 'Descartado':
        return 'block';
      case 'Error':
        return 'error';
      default:
        return 'pending';
    }
  }
  intentos(job: IDownlinkJob): IIntentoDownlink[] {
    return job.intentosLog ?? [];
  }
  tieneIntentos(job: IDownlinkJob): boolean {
    return (job.intentosLog?.length ?? 0) > 0;
  }
  intentosAbierto(id?: string): boolean {
    return !!id && this.intentosAbiertos().has(id);
  }
  toggleIntentos(id?: string): void {
    if (!id) return;
    this.intentosAbiertos.update((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ─── paginación ──────────────────────────────────────────────

  itemsVisibles(): ItemComando[] {
    return this.items().slice(0, this.visibles());
  }
  restantes(): number {
    return Math.max(0, this.items().length - this.visibles());
  }
  mostrarMas(): void {
    this.visibles.update((n) => n + this.PAGINA);
  }
  hayExpandidos(): boolean {
    return this.visibles() > this.PAGINA;
  }
  mostrarMenos(): void {
    this.visibles.set(this.PAGINA);
  }

  toggleAyuda(): void {
    this.mostrarAyuda.update((v) => !v);
  }

  // trackBy para el *ngFor de la línea de tiempo.
  trackItem = (_: number, it: ItemComando) =>
    it.job._id ?? it.job.fechaCreacion;
}
